import { StoreModule } from '@ngrx/store';
import { reducers } from './app.state';
import { metaReducers } from './app.meta-reducers';
import { extModules } from './build-specifics';
import { EffectsModule } from '@ngrx/effects';
import { NgModule } from '@angular/core';
import { AnalyticsEffects } from '@effects/analytic.effect';
import { OrganizationEffect } from '@effects/organization.effect';
import { SharedEffects } from '@effects/shared.effects';
import { AlertEventsEffect } from '@effects/alert-events.effect';
import { SearchConfigurationEffect } from '@effects/search-configuration.effect';
import { AlertMonitoringEffect } from '@effects/alert-monitoring.effect';
import { EdgeEditEffects } from '@effects/edge-edit.effects';
import { ArchiveEffect } from '@effects/archive.effect';
import { GrantedAccessEffect } from '@effects/granted-access.effect';
import { StatsEffects } from '@effects/stats.effects';
import { UserSettingsEffect } from '@effects/user-settings.effect';
import { GeneralEffect } from '@effects/general.effects';
import { ActivityLogEffect } from '@effects/activity-log.effect';
import { CameraEffect } from '@effects/camera.effect';
import { MultiSearchEffect } from '@effects/multi-search.effect';
import { WebRtcEffect } from '@effects/webrtc.effect';
import { MultiPlaybackEffect } from '@effects/multi-playback.effect';
import { PricingEffect } from '@effects/pricing.effect';
import { AlertsV2Effect } from '@effects/alerts-v2.effect';
import { PeopleEffect } from '@effects/people.effect';
import { LocationEditEffects } from '@effects/location-edit.effects';
import { OperatorEffects } from '@effects/operator.effects';
import { StorageEffects } from '@effects/storage.effects';
import { AlertLogEffect } from '@effects/alert-log.effect';
import { DoorsEffect } from '@effects/doors.effect';
import { CustomEventsEffect } from '@effects/custom-events.effect';
import { ApiKeyEffect } from '@effects/api-key.effect';
import { UserGroupsEffect } from '@effects/user-groups.effect';
import { VehiclesEffect } from '@effects/vehicles.effect';
import { UniformsEffect } from '@effects/uniforms.effect';
import { CustomObjectsEffect } from '@effects/custom-objects.effect';
import { PermissionGroupsEffect } from '@effects/permission-groups.effect';
import { OrgGroupsEffect } from '@effects/org-groups.effect';
import { DashboardEffects } from '@effects/dashboard.effects';
import { DeviceStatusEffects } from '@effects/device-status.effects';
import { AuthenticationEffects } from '@effects/authentication.effects';
import { UserEffects } from '@effects/user.effects';
import { LocationEffects } from '@effects/location.effects';
import { EdgeEffects } from '@effects/edge.effects';
import { CameraEffects } from '@effects/camera.effects';
import { IdentityEffect } from '@effects/identity.effect';
import { AppleTvEffects } from '@effects/apple-tv.effects';
import { UserDeviceEffects } from '@effects/user-device.effects';
import { IntegrationsEffect } from '@effects/integrations.effect';
import { MfaEffects } from '@effects/mfa.effects';
import { HttpRequestEffects } from '@effects/http-request.effects';
import { FloorEffects } from '@effects/floor.effects';
import { DhcpEffects } from '@effects/dhcp.effects';
import { ExternalStorageEffects } from '@effects/external-storage.effects';
import { PhysicalAccessControlEffects } from '@effects/physical-access-control.effects';
import { RemoteIOEffects } from '@effects/remote-io.effects';
import { WallV2Effects } from '@effects/wall-v2.effects';
import { YoutubeEffects } from '@effects/youtube.effects';
import { AccessDoorsEffects } from '@effects/access-doors.effects';
import { PrivateStorageEffects } from '@effects/private-storage.effects';
import { CameraSettingsEffect } from '@effects/camera-settings.effect';
import { HomeEffects } from '@effects/home.effects';
import { AlarmEffects } from '@effects/alarm.effects';
import { ScheduleEffects } from '@effects/schedule.effects';
import { SyncQueueEffects } from '@effects/sync-queue.effects';
import { EdgeMetadataEffects } from '@effects/edge-metadata.effect';
import { environment } from '../../environments/environment';
import { AlarmManagementEffects } from '@states/alarm-mangment/alarm-management.effects';
import { AlarmsManagerEffects } from '@effects/alarms-manager.effects';
import { LiveKitEffects } from '@effects/live-kit.effects';
import { PlaybackMonitoringEffects } from '@effects/playback-monitoring.effects';
import { UnSyncedEdgesEffects } from '@effects/unsynced-edges.effects';
import { ArchiveStatsEffects } from '@effects/archive-stats.effects';
import { JobEffects } from '@effects/job.effects';
import { OrgVariablesEffects } from '@effects/org-variables.effects';
import { ControlEventsEffect } from '@effects/control-events.effect';
import { SearchableEffects } from '@effects/searchable.effects';
import { SensorsEffects } from '@effects/sensors.effects';
import { AnalyticsSyncEffects } from '@effects/analytics-sync.effects';
import { OAuthProviderEffect } from '@effects/oauth-provider.effect';
import { MicrosoftTeamsEffects } from '@effects/microsoft-teams.effects';
import { AvailableDevicesEffect } from '@effects/available-devices.effects';
import { IssuesEffects } from '@effects/issues.effects';
import { DatabaseTagsEffect } from '@effects/database-tags.effect';
import { EntityTagEffect } from '@effects/entity-tag.effect';
import { MapCustomObjectsEffects } from '@effects/map-custom-objects.effects';
import { ShelfsEffect } from '@effects/shelfs.effect';
import { MapEffects } from '@effects/map.effects';
import { SupportOrganizationLogEffects } from '@effects/support-organization-log.effects';
import { CasePreviewAuditEffect } from '@effects/case-preview-audit.effect';
import { MonitoringEffects } from '@effects/monitoring.effects';
import { CertificateEffects } from '@effects/certificate.effects';
import { OAuth2CredentialsEffect } from '@effects/oauth2-credentials.effect';
import { SupportAccessEffects } from '@effects/support-access.effects';
import { NotificationTemplatesEffects } from '@effects/notification-templates.effects';
import { contactGroupsFeature } from '@states/contact-groups/contact-groups.feature';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { notificationTemplatesFeature } from '@states/notification-templates/notification-templates.feature';
import { zoneRegionsFeature } from '@states/zone-regions/zone-regions.feature';
import { zoneRegionsPickerFeature } from '@states/zone-regions-picker/zone-regions-picker.feature';
import { zoneRegionEditorFeature } from '@states/zone-region-editor/zone-region-editor.feature';
import { cameraZoneRegionsFeature } from '@states/camera-zone-regions/camera-zone-regions.feature';
import { shareableGroupsSyncFeature } from '@states/shareable-groups-sync/shareable-groups-sync.feature';
import {
  startLoadingContactGroups$,
  getContactGroups$,
  loadContactGroupMembers$,
  startLoadingContactGroupMembers$,
  startCreateContactGroup$,
  createContactGroup$,
  startUpdateContactGroup$,
  updateContactGroup$,
  startUpdateContactGroupMembers$,
  updateContactGroupMembers$,
  startRemoveContactGroupMember$,
  removeContactGroupMember$,
  startDeleteContactGroup$,
  deleteContactGroup$,
} from '@effects/contact-groups.effects';
import {
  startTriggerPersonSync$,
  triggerPersonSync$,
  startTriggerVehicleSync$,
  triggerVehicleSync$,
  startLoadingSyncLogs$,
  loadSyncLogs$,
  startLoadingSyncStats$,
  loadSyncStats$,
  refreshShareableGroupsSyncData$,
} from '@effects/shareable-groups-sync.effects';
import {
  startLoadingPersonGroups$,
  loadPersonGroups$,
  startLoadingPersonGroupDetails$,
  loadPersonGroupDetails$,
  loadPersonGroupMembers$,
  joinPersonGroup$,
  createPersonGroup$,
  updatePersonGroup$,
  startLoadingPersonGroupShareOptions$,
  loadPersonGroupAccessList$,
  loadPersonGroupInviteCode$,
  updatePersonGroupAccessListItem$,
  removePersonGroupAccessListItem$,
  requestRemovePersonGroupAccess$,
  requestRemovePersonsFromGroup$,
  removePersonsFromGroup$,
  startAddingPersonsToGroups$,
  addPersonsToGroups$,
  deletePersonGroup$,
  requestDeletePersonGroup$,
} from '@effects/person-groups.effects';
import {
  startLoadingZoneRegionCameras$,
  loadZoneRegionCameras$,
  startSaveZoneRegionsWithCameras$,
  saveZoneRegionsWithCameras$,
  startDeleteZoneRegionCamera$,
  startLoadingZoneRegions$,
  getZoneRegions$,
  startCreateZoneRegion$,
  createZoneRegion$,
  startUpdateZoneRegionCamera$,
  updateZoneRegionCamera$,
  startRenameZoneRegion$,
  renameZoneRegion$,
  startSetZoneCounting$,
  setZoneCounting$,
  startDeleteZoneRegion$,
  deleteZoneRegion$,
} from '@effects/zone-regions.effects';
import {
  getZoneRegionsPicker$,
  loadAllPickerCameras$,
  startLoadingZoneRegionsPicker$,
  syncZoneRegionsPickerDeleteSuccess$,
  syncZoneRegionsPickerCreateSuccess$,
  syncZoneRegionsPickerSaveSuccess$,
} from '@effects/zone-regions-picker.effects';
import {
  startLoadingAllZoneRegions$,
  loadAllZoneRegions$,
  syncZoneRegionsDeleteSuccess$,
} from '@effects/zone-region-editor.effects';
import { caseManagementFeature } from '@states/case-management/case-management.feature';
import { CaseManagementEffect } from '@effects/case-management.effect';
import { caseAccessControlEventFeature } from '@states/case-access-control-event/case-access-control-event.feature';
import { CaseAccessControlEventEffect } from '@effects/case-access-control-event.effect';
import {
  loadForCamera$,
  saveWithPayload$,
} from '@effects/camera-zone-regions.effects';
import { ptzPresetFeature } from '@states/ptz-presets/ptz-preset.feature';
import { ptzControlFeature } from '@states/ptz-control/ptz-control.feature';
import { runPtzTest$, sendPtzCommand$ } from '@effects/ptz-control.effects';
import {
  loadPtzPresets$,
  createPtzPreset$,
  renamePtzPreset$,
  redefinePtzPresetPosition$,
  deletePtzPreset$,
  setPtzPresetHomePreset$,
} from '@effects/ptz-presets.effects';
import { WorkflowEffect } from '@effects/workflow.effect';
import { WorkflowWebhooksEffect } from '@effects/workflow-webhooks.effect';
import { caseActivityLogFeature } from '@states/case-activity-log/case-activity-log.feature';
import { CaseActivityLogEffect } from '@effects/case-activity-log.effect';
import { caseCommentFeature } from '@states/case-comment/case-comment.feature';
import { CaseCommentEffect } from '@effects/case-comment.effect';
import { caseCategoriesFeature } from '@states/case-category/case-category.feature';
import { CaseCategoryEffect } from '@effects/case-category.effect';
import { caseAttachmentFeature } from '@states/case-attachment/case-attachment.feature';
import { CaseAttachmentEffect } from '@effects/case-attachment.effect';
import { caseFileRequestFeature } from '@states/case-file-request/case-file-request.feature';
import { CaseFileRequestEffect } from '@effects/case-file-request.effect';
import { CaseArchiveEffect } from '@effects/case-archive.effect';
import { caseArchiveFeature } from '@states/case-archive/case-archive.feature';
import { CaseAnnotationEffect } from '@effects/case-annotation.effect';
import { caseAnnotationFeature } from '@states/case-annotation/case-annotation.feature';
import { accountWallFeature } from '@states/account-wall/account-wall.feature';
import {
  addWallToAccount$,
  removeWallFromAccount$,
  getAllWalls$,
  getNextPage$,
  onQueryParamsChanged$,
  filterChangeTriggersReload$,
} from '@effects/account-wall.effects';
import { filePreviewFeature } from '@states/file-preview/file-preview.feature';
import { FilePreviewEffect } from '@effects/file-preview.effect';
import { supportUsersFeature } from '@states/support-users/support-users.feature';
import { casePreviewAuditFeature } from '@states/case-preview-audit/case-preview-audit.feature';
import {
  loadSupportUsers$,
  filterTrigger$,
  startLoadingSupportUser$,
} from '@effects/support-users.effects';
import {
  bulkCreatePendingPersons$,
  bulkDeleteSavedPersons$,
  attachPersonImage$,
  createPerson$,
  getSavedPersons$,
  getSuggestedTrackedPersons$,
  getUnsavedPersons$,
  syncPersonImageUploadProgress$,
  trainSavedPerson$,
  updateSavedPerson$,
  uploadPersonImage$,
} from '@effects/person.effects';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import {
  addVehiclesToGroups$,
  createVehicleGroup$,
  deleteVehicleGroup$,
  loadVehicleGroups$,
  removeVehiclesFromGroup$,
  requestDeleteVehicleGroup$,
  requestRemoveVehiclesFromGroup$,
  startAddingVehiclesToGroups$,
  updateVehicleGroup$,
  updateVehicleGroupAccessListItem$,
  requestRemoveVehicleGroupAccess$,
  removeVehicleGroupAccessListItem$,
  joinVehicleGroup$,
  loadVehicleGroupInviteCode$,
  startLoadingVehicleGroupShareOptions$,
  loadVehicleGroupAccessList$,
  startLoadingVehicleGroupDetails$,
  loadVehicleGroupDetails$,
  loadVehicleGroupMembers$,
} from '@effects/vehicle-groups.effects';
import { provideShareChat } from '@share-chat';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { OrganizationsActions } from '@states/organization/organization.actions-types';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { UserActions } from '@states/user/user.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { grantedAccessSchemaV2Feature } from './states/granted-access-schema-v2/ granted-access-schema-v2.feature';
import {
  loadGrantedAccessSchemaV2$,
  revokeGrantedAccessSchemaV2Access$,
} from './effects/ granted-access-schema-v2.effect';
import { navigationTreeFeature } from './states/navigation-tree/navigation-tree.feature';
import {
  getNavigationTreeOrdering$,
  saveNavigationOrderKeys$,
  saveNavigationOrderKeysToStore$,
} from './effects/navigation-tree.effect';

@NgModule({
  imports: [
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: !environment.production,
        strictActionImmutability: false,
        strictActionSerializability: false,
        strictStateSerializability: !environment.production,
      },
    }),
    StoreModule.forFeature(contactGroupsFeature),
    StoreModule.forFeature(personGroupsFeature),
    StoreModule.forFeature(vehicleGroupsFeature),
    StoreModule.forFeature(notificationTemplatesFeature),
    StoreModule.forFeature(zoneRegionsFeature),
    StoreModule.forFeature(zoneRegionsPickerFeature),
    StoreModule.forFeature(zoneRegionEditorFeature),
    StoreModule.forFeature(cameraZoneRegionsFeature),
    StoreModule.forFeature(ptzPresetFeature),
    StoreModule.forFeature(ptzControlFeature),
    StoreModule.forFeature(caseManagementFeature),
    StoreModule.forFeature(caseAccessControlEventFeature),
    StoreModule.forFeature(caseActivityLogFeature),
    StoreModule.forFeature(caseCommentFeature),
    StoreModule.forFeature(caseCategoriesFeature),
    StoreModule.forFeature(caseAttachmentFeature),
    StoreModule.forFeature(caseFileRequestFeature),
    StoreModule.forFeature(caseArchiveFeature),
    StoreModule.forFeature(caseAnnotationFeature),
    StoreModule.forFeature(accountWallFeature),
    StoreModule.forFeature(filePreviewFeature),
    StoreModule.forFeature(supportUsersFeature),
    StoreModule.forFeature(casePreviewAuditFeature),
    StoreModule.forFeature(shareableGroupsSyncFeature),
    StoreModule.forFeature(grantedAccessSchemaV2Feature),
    StoreModule.forFeature(navigationTreeFeature),
    provideShareChat({
      config: {
        apiUrl: environment.apiLumana,
        env: environment.env,
      },
      authentication: {
        actions: {
          logoutSuccess: AuthenticationActions.LogoutSuccess,
        },
      },
      user: {
        actions: {
          updateProfileSuccess: UserActions.UpdateUserProfileSuccess,
        },
      },
      organization: {
        selectors: {
          selectActiveOrganizationChatStatus:
            OrganizationSelectors.selectActiveOrganizationChatStatus,
          selectActiveOrganizationSynapseSpaceId:
            OrganizationSelectors.selectActiveOrganizationSynapseSpaceId,
        },
        actions: {
          getActiveOrganization: OrganizationsActions.getActiveOrganization,
          setActiveOrganization: OrganizationsActions.setActiveOrganization,
          updateActiveOrganizationSuccess:
            OrganizationsActions.updateActiveOrganizationSuccess,
        },
      },
      shared: {
        actions: {
          showMessage: SharedActions.showMessage,
        },
      },
    }),
    extModules,
    EffectsModule.forRoot([
      AuthenticationEffects,
      UserEffects,
      SyncQueueEffects,
      AlarmManagementEffects,
      AlarmsManagerEffects,
      LocationEffects,
      SearchableEffects,
      EdgeEffects,
      CameraEffects,
      AnalyticsEffects,
      OrganizationEffect,
      SharedEffects,
      AlertEventsEffect,
      AlertsV2Effect,
      WebRtcEffect,
      PricingEffect,
      MultiSearchEffect,
      MultiPlaybackEffect,
      CameraEffect,
      SearchConfigurationEffect,
      AlertMonitoringEffect,
      EdgeEditEffects,
      ArchiveEffect,
      GeneralEffect,
      GrantedAccessEffect,
      StatsEffects,
      UserSettingsEffect,
      ActivityLogEffect,
      PeopleEffect,
      LocationEditEffects,
      OperatorEffects,
      StorageEffects,
      AlertLogEffect,
      DoorsEffect,
      VehiclesEffect,
      UniformsEffect,
      CustomObjectsEffect,
      CustomEventsEffect,
      ControlEventsEffect,
      ApiKeyEffect,
      MicrosoftTeamsEffects,
      UserGroupsEffect,
      OrgGroupsEffect,
      PermissionGroupsEffect,
      DashboardEffects,
      DeviceStatusEffects,
      IdentityEffect,
      AppleTvEffects,
      UserDeviceEffects,
      IntegrationsEffect,
      MfaEffects,
      HttpRequestEffects,
      FloorEffects,
      DhcpEffects,
      WallV2Effects,
      ExternalStorageEffects,
      PhysicalAccessControlEffects,
      RemoteIOEffects,
      YoutubeEffects,
      AccessDoorsEffects,
      PrivateStorageEffects,
      CameraSettingsEffect,
      HomeEffects,
      AlarmEffects,
      ScheduleEffects,
      EdgeMetadataEffects,
      LiveKitEffects,
      PlaybackMonitoringEffects,
      UnSyncedEdgesEffects,
      ArchiveStatsEffects,
      JobEffects,
      OrgVariablesEffects,
      SensorsEffects,
      AnalyticsSyncEffects,
      OAuthProviderEffect,
      AvailableDevicesEffect,
      IssuesEffects,
      DatabaseTagsEffect,
      EntityTagEffect,
      MapCustomObjectsEffects,
      ShelfsEffect,
      MapEffects,
      SupportOrganizationLogEffects,
      CasePreviewAuditEffect,
      SupportAccessEffects,
      CertificateEffects,
      SupportOrganizationLogEffects,
      MonitoringEffects,
      OAuth2CredentialsEffect,
      NotificationTemplatesEffects,
      CaseManagementEffect,
      CaseAccessControlEventEffect,
      CaseActivityLogEffect,
      CaseCommentEffect,
      CaseAttachmentEffect,
      CaseFileRequestEffect,
      WorkflowEffect,
      WorkflowWebhooksEffect,
      CaseCategoryEffect,
      CaseArchiveEffect,
      CaseAnnotationEffect,
      FilePreviewEffect,
    ]),
    EffectsModule.forFeature({
      startLoadingContactGroups$,
      getContactGroups$,
      startLoadingContactGroupMembers$,
      loadContactGroupMembers$,
      startCreateContactGroup$,
      createContactGroup$,
      startUpdateContactGroup$,
      updateContactGroup$,
      startUpdateContactGroupMembers$,
      updateContactGroupMembers$,
      startRemoveContactGroupMember$,
      removeContactGroupMember$,
      startDeleteContactGroup$,
      deleteContactGroup$,
      startTriggerPersonSync$,
      triggerPersonSync$,
      startTriggerVehicleSync$,
      triggerVehicleSync$,
      startLoadingSyncLogs$,
      loadSyncLogs$,
      startLoadingSyncStats$,
      loadSyncStats$,
      refreshShareableGroupsSyncData$,
      startLoadingPersonGroups$,
      loadPersonGroups$,
      startLoadingPersonGroupDetails$,
      loadPersonGroupDetails$,
      loadPersonGroupMembers$,
      joinPersonGroup$,
      createPersonGroup$,
      updatePersonGroup$,
      requestDeletePersonGroup$,
      deletePersonGroup$,
      startLoadingPersonGroupShareOptions$,
      loadPersonGroupAccessList$,
      loadPersonGroupInviteCode$,
      updatePersonGroupAccessListItem$,
      removePersonGroupAccessListItem$,
      requestRemovePersonGroupAccess$,
      requestRemovePersonsFromGroup$,
      removePersonsFromGroup$,
      startAddingPersonsToGroups$,
      addPersonsToGroups$,
      bulkCreatePendingPersons$,
      bulkDeleteSavedPersons$,
      updateSavedPerson$,
      startLoadingZoneRegionCameras$,
      loadZoneRegionCameras$,
      startSaveZoneRegionsWithCameras$,
      saveZoneRegionsWithCameras$,
      startDeleteZoneRegionCamera$,
      startLoadingZoneRegions$,
      getZoneRegions$,
      startLoadingAllZoneRegions$,
      loadAllZoneRegions$,
      syncZoneRegionsDeleteSuccess$,
      startLoadingZoneRegionsPicker$,
      getZoneRegionsPicker$,
      syncZoneRegionsPickerDeleteSuccess$,
      syncZoneRegionsPickerCreateSuccess$,
      syncZoneRegionsPickerSaveSuccess$,
      loadAllPickerCameras$,
      loadForCamera$,
      saveWithPayload$,
      startCreateZoneRegion$,
      createZoneRegion$,
      startUpdateZoneRegionCamera$,
      updateZoneRegionCamera$,
      startRenameZoneRegion$,
      renameZoneRegion$,
      startSetZoneCounting$,
      setZoneCounting$,
      startDeleteZoneRegion$,
      deleteZoneRegion$,
      addWallToAccount$,
      removeWallFromAccount$,
      getAllWalls$,
      getNextPage$,
      onQueryParamsChanged$,
      filterChangeTriggersReload$,
      loadSupportUsers$,
      filterTrigger$,
      startLoadingSupportUser$,
      getSavedPersons$,
      getSuggestedTrackedPersons$,
      getUnsavedPersons$,
      syncPersonImageUploadProgress$,
      uploadPersonImage$,
      createPerson$,
      trainSavedPerson$,
      attachPersonImage$,
      loadVehicleGroups$,
      createVehicleGroup$,
      requestDeleteVehicleGroup$,
      deleteVehicleGroup$,
      updateVehicleGroup$,
      startAddingVehiclesToGroups$,
      addVehiclesToGroups$,
      requestRemoveVehiclesFromGroup$,
      removeVehiclesFromGroup$,
      updateVehicleGroupAccessListItem$,
      requestRemoveVehicleGroupAccess$,
      removeVehicleGroupAccessListItem$,
      joinVehicleGroup$,
      loadVehicleGroupInviteCode$,
      startLoadingVehicleGroupShareOptions$,
      loadVehicleGroupAccessList$,
      startLoadingVehicleGroupDetails$,
      loadVehicleGroupDetails$,
      loadVehicleGroupMembers$,
      loadGrantedAccessSchemaV2$,
      revokeGrantedAccessSchemaV2Access$,
      saveNavigationOrderKeysToStore$,
      saveNavigationOrderKeys$,
      getNavigationTreeOrdering$,
      loadPtzPresets$,
      createPtzPreset$,
      renamePtzPreset$,
      redefinePtzPresetPosition$,
      deletePtzPreset$,
      setPtzPresetHomePreset$,
      sendPtzCommand$,
      runPtzTest$,
    }),
  ],
})
export class AppStoreModule {}
